import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';

import BackButton from '../../../custom/BackButton';
import ActionIcon from '../../../custom/ActionIcon';
import { createdUpdatedInfo, getGuid } from '../../../../utils';
import { UldArray, Uld } from './CreateTransferExport';
import { Input } from 'reactstrap';
import _ from 'lodash';
import ModalConfirmDelete from './ModalConfirmDelete';
import { Button } from 'reactstrap';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    uldArray: UldArray;
    setUldArray: React.Dispatch<React.SetStateAction<UldArray>>;
    selectedUld: Uld | null;
}

export default function ModalUld({
    modal,
    setModal,
    uldArray,
    setUldArray,
    selectedUld
}: Props) {

    const update = Boolean(selectedUld);
    const [addUld, setAddUld] = useState('');
    const [error, setError] = useState('');
    const useAddUld = addUld.toUpperCase();
    const [modalConfirmDelete, setModalConfirmDelete] = useState(false);
    const existingBulkUld = uldArray.some(item => item && item.uld === 'BULK');

    useEffect(() => {
        if (selectedUld) {
            setAddUld(selectedUld.uld);
        } else {
            setAddUld('');
        }
        setError('');
    }, [selectedUld, modal]);

    useEffect(() => {
        if (!useAddUld.trim()) {
            setError('ULD cannot be empty');
            return;
        }

        const existingUld = uldArray.find(item => item && item.uld === useAddUld && item.key !== selectedUld?.key);
        if (existingUld) {
            setError('ULD already exists');
            return;
        }

        setError('');
    }, [useAddUld, uldArray]);

    const handleAddUpdateUldKey = (overrideUseAddUld?: string) => {
        const addUld = overrideUseAddUld || useAddUld;
        setUldArray(prev => {
            const copy = _.cloneDeep(prev);
            if (update) {
                const index = copy.findIndex(item => item.key === selectedUld.key);
                if (index !== -1) {
                    copy[index].uld = addUld; // ✅ Correctly update `uld`
                }
            } else {
                copy.push({
                    key: getGuid(),
                    uld: addUld,
                    data: []
                });
            }
            return copy;
        });
        toggle();
    };

    const launchDeleteUld = () => {
        setModalConfirmDelete(true);
    }

    const handleDeleteUld = () => {
        if (!selectedUld) return;
        setUldArray(prev => prev.filter(item => item.key !== selectedUld.key));
        setModalConfirmDelete(false);
        toggle();
    };

    const toggle = () => setModal(!modal);

    return (
        <>
            <Modal isOpen={modal} toggle={toggle}>
                <ModalHeader className={'d-flex'}>
                    <HeaderContainer>
                        <BackButton onClick={toggle} />
                        <h4 className={'pl-2'}>{update ? 'Update' : 'Create'} ULD</h4>
                    </HeaderContainer>
                </ModalHeader>
                <ModalBody>
                    <Label>Enter ULD Serial Number</Label>
                    <Input
                        value={addUld}
                        onChange={e => setAddUld(e.target.value)}
                    />
                    {error && <p style={{ color: 'red' }}>{error}</p>}
                </ModalBody>
                <ExpandedFooter>
                    <FooterContentContainer>
                        <FooterButtonsContainer update={true}>
                            <RenderRightButton 
                                update={update}
                                launchDeleteUld={launchDeleteUld}
                                existingBulkUld={existingBulkUld}   
                                handleAddUpdateUldKey={handleAddUpdateUldKey}           
                            />
                            <ActionIcon
                                type={'save'}
                                disabled={!useAddUld.trim() || error.length > 0}
                                onClick={handleAddUpdateUldKey}
                            />
                        </FooterButtonsContainer>
                    </FooterContentContainer>
                </ExpandedFooter>
            </Modal>
            <ModalConfirmDelete
                modal={modalConfirmDelete}
                setModal={setModalConfirmDelete}
                description={`ULD ${_.get(selectedUld, 'uld', '')}`}
                confirmDelete={handleDeleteUld}
            />
        </>
    );
}

const RenderRightButton = ({
    update,
    launchDeleteUld,
    existingBulkUld,
    handleAddUpdateUldKey
}: {
    update: boolean;
    launchDeleteUld: () => void;
    existingBulkUld: boolean;
    handleAddUpdateUldKey: (overrideUseAddUld?: string) => void;
}) => {
    if (update) {
        return (
            <ActionIcon
                type={'delete'}
                onClick={launchDeleteUld}
            />
        );
    }

    return (
        <Button 
            disabled={existingBulkUld}
            onClick={() => handleAddUpdateUldKey('BULK')}
        >
                BULK
        </Button>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;